import { FC, ReactNode, useMemo } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Typography } from '@mui/material';
import { ReactComponent as QuestionSVG } from 'assets/icons/question.svg';
import { ProviderAccordion, ProviderPreview } from 'components/shared';
import { CONSOLIDATED_PAYMENT_INSTRUCTION_TITLE, PAYMENT_INSTRUCTION_TITLE } from 'utils/constants';
import {
	convertCoinsToUAH,
	getAmountTitle,
	getPayerFullName,
	getServicePaymentInstructionNumber,
	getServicePaymentInstructionServiceName,
	getServicePaymentRecipientCount,
	groupServicePaymentInstructionItemsByRecipient,
} from 'utils/helpers';
import {
	INormalizedServiceBillRequestItem,
	IPaymentPayerFormData,
	IServicePaymentInstructionGroup,
} from 'utils/types';
import styles from './index.module.scss';

interface ServicePaymentInstructionProps {
	services: INormalizedServiceBillRequestItem[];
	payerData?: IPaymentPayerFormData | null;
	icon?: string;
}

interface DetailRowProps {
	label: string;
	value?: string | number | null;
}

const DetailRow: FC<DetailRowProps> = ({ label, value }) => {
	if (value === undefined || value === null || value === '') return null;

	return (
		<div className={styles.details__row}>
			<Typography variant="body2" color="var(--color-neutral-500)">
				{label}
			</Typography>
			<Typography variant="body2">{value}</Typography>
		</div>
	);
};

const InstructionSection: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
	<section className={styles.section}>
		<Typography variant="h6">{title}</Typography>
		<div className={styles.section__list}>{children}</div>
	</section>
);

const getUniqueValues = (values: (string | number | null | undefined)[], separator = '; '): string =>
	Array.from(
		new Set(
			values
				.map((value) => value?.toString().trim())
				.filter((value): value is string => Boolean(value))
		)
	).join(separator);

const ServiceInstructionItem: FC<{ group: IServicePaymentInstructionGroup; icon?: string }> = ({
	group,
	icon = '',
}) => {
	const firstService = group.services[0];
	const amount = convertCoinsToUAH(
		group.services.reduce((total, service) => total + Number(service.incomeBalance ?? 0), 0)
	);
	const instructionNumber = getServicePaymentInstructionNumber(group.services);
	const ibans = getUniqueValues(group.services.map((service) => service.regDataObject.account));
	const recipientNames = getUniqueValues(group.services.map((service) => service.recipientName));
	const serviceNames = getUniqueValues(
		group.services.map(getServicePaymentInstructionServiceName),
		', '
	);
	const title = recipientNames || serviceNames;
	const description = getUniqueValues(
		[
			...group.services.map(getServicePaymentInstructionServiceName),
			...group.services.map((service) => service.regDataObject.description),
		]
	);

	return (
		<ProviderAccordion
			className={styles.accordion}
			expandIcon={<ExpandMoreRoundedIcon />}
			title={
				<ProviderPreview
					title={title}
					description={
						<>
							{`IBAN: ${ibans || '—'}, `}
							<span className={styles.previewAmount}>{getAmountTitle(amount)}</span>
						</>
					}
					icon={icon}
					code={firstService.code}
					providerType={firstService.providerType}
					isHideQuestion
					isCompact
				/>
			}
		>
			<div className={styles.details}>
				<DetailRow label="Номер платіжної інструкції" value={instructionNumber} />
				<DetailRow label="Дата" value={getUniqueValues(group.services.map((service) => service.tariffItem.date))} />
				<DetailRow
					label="Одержувач"
					value={getUniqueValues(group.services.map((service) => service.recipientName))}
				/>
				<DetailRow label="IBAN отримувача" value={ibans} />
				<DetailRow
					label="ЄДРПОУ"
					value={getUniqueValues(group.services.map((service) => service.regDataObject.zkpo))}
				/>
				<DetailRow
					label="Банк отримувача"
					value={getUniqueValues(group.services.map((service) => service.regDataObject.bankName))}
				/>
				<DetailRow
					label="МФО банку"
					value={getUniqueValues(group.services.map((service) => service.regDataObject.bankMfo))}
				/>
				<DetailRow
					label="Особовий рахунок"
					value={getUniqueValues(group.services.map((service) => service.providerAccountId))}
				/>
				<DetailRow label="Призначення платежу" value={description} />
				<DetailRow label="Сума до сплати" value={getAmountTitle(amount)} />
			</div>
		</ProviderAccordion>
	);
};

const getUltimateDebtor = (payerData?: IPaymentPayerFormData | null) => {
	if (!payerData) return null;
	return payerData.isPayerSameAsUltimateDebtor ? payerData : payerData.ultimateDebtor;
};

export const ServicePaymentInstruction: FC<ServicePaymentInstructionProps> = ({ services, payerData, icon }) => {
	const ultimateDebtor = getUltimateDebtor(payerData);
	const instructionGroups = useMemo(() => groupServicePaymentInstructionItemsByRecipient(services), [services]);
	const instructionTitle =
		getServicePaymentRecipientCount(services) > 1
			? CONSOLIDATED_PAYMENT_INSTRUCTION_TITLE
			: PAYMENT_INSTRUCTION_TITLE;

	return (
		<div className={styles.wrapper}>
			<div className={styles.confirmation}>
				<QuestionSVG className={styles.confirmation__icon} />
				<Typography variant="subtitle1">{instructionTitle}</Typography>
			</div>
			<InstructionSection title="Інші платежі">
				{instructionGroups.map((group) => (
					<ServiceInstructionItem group={group} icon={icon} key={group.key} />
				))}
			</InstructionSection>
			{payerData && ultimateDebtor && (
				<section className={styles.payer}>
					<Typography variant="h6">Введені дані</Typography>
					<div className={styles.payer__persons}>
						<div className={styles.payer__person}>
							<Typography variant="subtitle2">Платник</Typography>
							<div className={styles.payer__data}>
								<div>
									<Typography variant="body2" color="var(--color-neutral-500)">
										ПІБ
									</Typography>
									<Typography variant="body1">{getPayerFullName(payerData)}</Typography>
								</div>
								<div>
									<Typography variant="body2" color="var(--color-neutral-500)">
										ІПН (РНОКПП)
									</Typography>
									<Typography variant="body1">{payerData.taxId}</Typography>
								</div>
							</div>
						</div>
						<div className={styles.payer__person}>
							<Typography variant="subtitle2">Фактичний платник</Typography>
							<div className={styles.payer__data}>
								<div>
									<Typography variant="body2" color="var(--color-neutral-500)">
										ПІБ
									</Typography>
									<Typography variant="body1">{getPayerFullName(ultimateDebtor)}</Typography>
								</div>
								<div>
									<Typography variant="body2" color="var(--color-neutral-500)">
										ІПН (РНОКПП)
									</Typography>
									<Typography variant="body1">{ultimateDebtor.taxId}</Typography>
								</div>
							</div>
						</div>
					</div>
				</section>
			)}
		</div>
	);
};
